import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Save, Plus, Trash2, Loader2, GripVertical, Megaphone } from "lucide-react";
import { toast } from "sonner";

export function AdminHomepageEditor() {
  const [content, setContent] = useState(null);
  const [announcement, setAnnouncement] = useState({ enabled: false, text: "", link: "" });
  const [saving, setSaving] = useState(false);
  const [savingAnn, setSavingAnn] = useState(false);

  useEffect(() => {
    api.get("/site-content/homepage").then(({ data }) => setContent(data)).catch(() => {});
    api.get("/announcement").then(({ data }) => setAnnouncement(data)).catch(() => {});
  }, []);

  function updateField(key, value) {
    setContent((c) => ({ ...c, [key]: value }));
  }

  function updateFeature(i, key, value) {
    const features = [...(content?.features || [])];
    features[i] = { ...features[i], [key]: value };
    updateField("features", features);
  }

  function updateFaq(i, key, value) {
    const faq = [...(content?.faq || [])];
    faq[i] = { ...faq[i], [key]: value };
    updateField("faq", faq);
  }

  function addFaq() {
    updateField("faq", [...(content?.faq || []), { q: "", a: "" }]);
  }

  function removeFaq(i) {
    updateField("faq", (content?.faq || []).filter((_, j) => j !== i));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.put("/admin/site-content/homepage", content);
      toast.success("Homepage content saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  }

  async function handleSaveAnnouncement() {
    setSavingAnn(true);
    try {
      await api.put("/admin/announcement", announcement);
      toast.success("Announcement bar saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingAnn(false); }
  }

  if (!content) return <div className="py-8 text-center text-slate-400">Loading...</div>;

  return (
    <div className="mt-4 space-y-4">
      {/* Announcement Bar */}
      <div className="bg-white rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-coral" />
            <h3 className="font-heading font-bold text-base">Announcement Bar</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{announcement.enabled ? "Live" : "Off"}</span>
            <Switch checked={announcement.enabled} onCheckedChange={(v) => setAnnouncement({ ...announcement, enabled: v })} />
          </div>
        </div>
        <Input value={announcement.text || ""} onChange={(e) => setAnnouncement({ ...announcement, text: e.target.value })}
          placeholder="Announcement text" className="rounded-xl text-sm" />
        <Input value={announcement.link || ""} onChange={(e) => setAnnouncement({ ...announcement, link: e.target.value })}
          placeholder="Link URL (optional)" className="rounded-xl text-sm" />
        <Button onClick={handleSaveAnnouncement} disabled={savingAnn} size="sm"
          className="bg-coral hover:bg-coral-hover text-white rounded-full font-bold">
          {savingAnn ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
        </Button>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-2xl p-5 space-y-3">
        <h3 className="font-heading font-bold text-base">Hero Section</h3>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Label (above headline)</label>
          <Input value={content.hero_label || ""} onChange={(e) => updateField("hero_label", e.target.value)}
            placeholder="e.g., Screen-swap play kits · Ages 3–8" className="rounded-xl text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Headline</label>
          <Input value={content.hero_title || ""} onChange={(e) => updateField("hero_title", e.target.value)}
            className="rounded-xl text-sm font-bold" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Subheadline</label>
          <Textarea value={content.hero_subtitle || ""} onChange={(e) => updateField("hero_subtitle", e.target.value)}
            rows={3} className="rounded-xl text-sm resize-none" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Primary button label</label>
            <Input value={content.hero_btn_primary || ""} onChange={(e) => updateField("hero_btn_primary", e.target.value)}
              placeholder="Get a free starter kit" className="rounded-xl text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Secondary button label</label>
            <Input value={content.hero_btn_secondary || ""} onChange={(e) => updateField("hero_btn_secondary", e.target.value)}
              placeholder="Browse play kits" className="rounded-xl text-sm" />
          </div>
        </div>
      </div>

      {/* Trust Items */}
      <div className="bg-white rounded-2xl p-5 space-y-3">
        <h3 className="font-heading font-bold text-base">Trust Items (4 icons below hero)</h3>
        {(content.features || []).map((f, i) => (
          <div key={i} className="border border-border rounded-xl p-3 space-y-2">
            <Input value={f.title || ""} onChange={(e) => updateFeature(i, "title", e.target.value)}
              placeholder="Title" className="rounded-lg text-sm font-semibold h-9" />
            <Input value={f.desc || ""} onChange={(e) => updateFeature(i, "desc", e.target.value)}
              placeholder="Sub-line" className="rounded-lg text-sm h-9" />
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-base">FAQ</h3>
          <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={addFaq}>
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        {(content.faq || []).map((f, i) => (
          <div key={i} className="border border-border rounded-xl p-3 space-y-2 relative">
            <Input value={f.q || ""} onChange={(e) => updateFaq(i, "q", e.target.value)}
              placeholder="Question" className="rounded-lg text-sm font-semibold h-9" />
            <Textarea value={f.a || ""} onChange={(e) => updateFaq(i, "a", e.target.value)}
              placeholder="Answer" rows={2} className="rounded-lg text-sm resize-none" />
            <Button size="sm" variant="ghost" className="absolute top-2 right-2 h-6 w-6 p-0 text-red-400 hover:text-red-600"
              onClick={() => removeFaq(i)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>

      <Button onClick={handleSave} disabled={saving}
        className="w-full bg-coral hover:bg-coral-hover text-white rounded-full font-bold h-11">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
        Save All Homepage Content
      </Button>
    </div>
  );
}
